import { Component, DestroyRef, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { throwError } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';

import { Globals } from '@core/globals';
import { ButtonComponent } from '@atoms/button/button.component';
import { FormComponent } from '@atoms/form/form.component';
import { FormInputsComponent } from '@atoms/form/form-inputs/form-inputs.component';
import { FormSectionComponent } from '@atoms/form/form-section/form-section.component';
import { FormTitleComponent } from '@atoms/form/form-title/form-title.component';
import { GridComponent } from '@atoms/grid/grid.component';
import { InputComponent } from '@atoms/input/input.component';
import { LinkComponent } from '@atoms/link/link.component';
import { ParagraphComponent } from '@atoms/paragraph/paragraph.component';
import { PhoneInputComponent } from '@atoms/phone-input/phone-input.component';
import { TextareaComponent } from '@atoms/textarea/textarea.component';
import { ContactInformationsComponent } from '@molecules/contact-informations/contact-informations.component';
import { FormThanksMessageComponent } from '@molecules/form-thanks-message/form-thanks-message.component';
import { ModalHeaderComponent } from '@molecules/modal/modal-header/modal-header.component';
import { ModalMainComponent } from '@molecules/modal/modal-main/modal-main.component';
import { ModalMainContentComponent } from '@molecules/modal/modal-main-content/modal-main-content.component';
import { ModalSidebarComponent } from '@molecules/modal/modal-sidebar/modal-sidebar.component';
import { ModalTitleComponent } from '@molecules/modal/modal-title/modal-title.component';
import { MakeModelSingleComponent } from '@organisms/make-model-single/make-model-single.component';
import { DemandRequest, DemandService } from '@services/demand.service';
import { FilterDataService } from '@services/filter-data.service';
import { GlobalsService } from '@services/globals.service';
import { GtmService } from '@services/gtm.service';

@Component({
  selector: 'app-car-demand-modal',
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    ButtonComponent,
    FormComponent,
    FormInputsComponent,
    FormSectionComponent,
    FormThanksMessageComponent,
    FormTitleComponent,
    GridComponent,
    InputComponent,
    LinkComponent,
    ParagraphComponent,
    PhoneInputComponent,
    TextareaComponent,
    ContactInformationsComponent,
    ModalHeaderComponent,
    ModalMainComponent,
    ModalMainContentComponent,
    ModalSidebarComponent,
    ModalTitleComponent,
    MakeModelSingleComponent,
  ],
  templateUrl: './car-demand-modal.component.html',
  styleUrl: './car-demand-modal.component.scss',
})
export class CarDemandModalComponent {
  private activatedRoute = inject(ActivatedRoute);
  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);
  public globals = inject(Globals);
  private demandService = inject(DemandService);
  public filterData = inject(FilterDataService);
  private globalsService = inject(GlobalsService);
  private gtmService = inject(GtmService);
  public form = this.fb.nonNullable.group({
    makeModel: [undefined as any, { nonNullable: true, validators: [Validators.required] }],
    name: ['', { nonNullable: true, validators: [Validators.required] }],
    email: ['', { nonNullable: true, validators: [Validators.required, Validators.email] }],
    phone: ['', { nonNullable: true, validators: [Validators.required] }],
    phoneCode: ['+420', [Validators.required]],
    message: ['', { nonNullable: true, validators: [] }],
  });
  private userIp: string = '';
  public step = 1;

  send(): void {
    if (this.form.invalid) return;
    const formData = this.form.getRawValue();
    const utmSource: string | null = this.activatedRoute.snapshot.queryParamMap.get('utm_source');
    const utmMedium: string | null = this.activatedRoute.snapshot.queryParamMap.get('utm_medium');
    const utmCampaign: string | null = this.activatedRoute.snapshot.queryParamMap.get('utm_campaign');
    const phoneCodeNumber: number | undefined = formData.phoneCode ? Number(formData.phoneCode.replace('+', '')) : undefined;
    const phoneCountry: number | undefined = this.globalsService.searchPhoneCountryCode(phoneCodeNumber);

    if (formData.makeModel === undefined) return;

    let message = formData.message;
    message += `, Poptávaný vůz: ${formData.makeModel.make} ${formData.makeModel.model}`;
    const demandRequest: DemandRequest = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      phoneCountryId: phoneCountry!,
      message: message,
      makeId: formData.makeModel.makeId ?? 0,
      modelId: formData.makeModel.modelId ?? 0,
      marketingAgree: false,
      customerIP: this.userIp,
      utmSource: utmSource,
      utmMedium: utmMedium,
      utmCampaign: utmCampaign,
    };
    this.demandService
      .sendForm(demandRequest)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.form.reset();
          this.form.markAsUntouched();
          this.step = 2;

          const dataLayer = {
            event: 'page_view',
            pageURL: '/' + this.globals.language() + '/podekovani-poptavkoveho-formulare',
            pageType: 'Purchase-3',
            pageTitle: 'Poděkování poptávkový formulář',
          };
          this.gtmService.pushGtm(dataLayer); 
        },
        error: (error) => {
          throwError(() => 'Something went wrong:' + error);
        },
      });
  }

  ngOnInit(): void {
    const dataLayer = {
      event: 'page_view',
      pageURL: '/' + this.globals.language() + '/poptavka-po-vozidle',
      pageType: 'Form-3',
      pageTitle: 'Poptávkový formulář',
    };
    this.gtmService.pushGtm(dataLayer);
    
    this.demandService
      .getUserIp()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.userIp = response.ip;
        },
        error: () => {
          this.userIp = '108.121.121.211'; // Error set placeholder
        },
      });
  }
}
